import Link from 'next/link';
import type { MediaEpisode } from '@akademi/site-builder-runtime';

export type EpisodeListStyle = 'list' | 'grid' | 'cards' | 'rail';

export interface EpisodeCardProps {
  episode: MediaEpisode;
  style?: EpisodeListStyle;
  /** Show "Episode N: " prefix on the title. */
  showEpisodeNumber?: boolean;
  active?: boolean;
  hrefTemplate?: string;
  /** Slug of the parent series — needed by the default href template. */
  seriesSlug?: string;
}

function fmtDuration(s?: number): string | undefined {
  if (typeof s !== 'number' || !Number.isFinite(s)) return undefined;
  const r = Math.round(s);
  const m = Math.floor(r / 60);
  return `${m}:${(r % 60).toString().padStart(2, '0')}`;
}

export function EpisodeCard({
  episode,
  style = 'list',
  showEpisodeNumber = true,
  active = false,
  hrefTemplate = '/tube/series/{seriesSlug}/play/{slug}',
  seriesSlug = '',
}: EpisodeCardProps) {
  const href = hrefTemplate
    .replace('{seriesSlug}', encodeURIComponent(seriesSlug))
    .replace('{slug}', encodeURIComponent(episode.slug));
  const titleWithNumber =
    showEpisodeNumber && episode.episodeNumber !== undefined
      ? `${episode.episodeNumber}. ${episode.title ?? episode.slug}`
      : (episode.title ?? episode.slug);

  if (style === 'rail') {
    return (
      <Link
        href={href}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          textDecoration: 'none',
          color: 'inherit',
          outline: active ? '2px solid var(--color-brand-500, #3b5bdb)' : 'none',
          outlineOffset: 2,
          borderRadius: 6,
        }}
      >
        <div
          aria-hidden
          style={{
            aspectRatio: '16 / 9',
            borderRadius: 'var(--radius-md, 10px)',
            background: episode.posterUrl
              ? `url(${episode.posterUrl}) center/cover no-repeat`
              : 'var(--color-surface-muted, #e4e4e7)',
            position: 'relative',
          }}
        >
          {episode.watched && (
            <span
              style={{
                position: 'absolute',
                top: 4,
                right: 4,
                background: 'rgba(0,0,0,0.7)',
                color: 'var(--color-text-on-brand, #fff)',
                fontSize: 'var(--font-size-xs, 12px)',
                padding: 'var(--space-0-5, 2px) var(--space-1, 4px)',
                borderRadius: 4,
              }}
            >
              ✓
            </span>
          )}
        </div>
        <div style={{ fontSize: 'var(--font-size-xs, 12px)', fontWeight: 600, lineHeight: 1.3 }}>
          {titleWithNumber}
        </div>
      </Link>
    );
  }

  // list / grid / cards — all use a thumb + body layout, varied by spacing
  return (
    <Link
      href={href}
      style={{
        display: 'grid',
        gridTemplateColumns: '160px 1fr auto',
        gap: 16,
        padding: 'var(--space-3, 12px) 0',
        textDecoration: 'none',
        color: 'inherit',
        borderBottom: '1px solid var(--color-border-subtle, rgba(0,0,0,0.06))',
        opacity: episode.watched ? 0.7 : 1,
      }}
    >
      <div
        aria-hidden
        style={{
          aspectRatio: '16 / 9',
          borderRadius: 6,
          background: episode.posterUrl
            ? `url(${episode.posterUrl}) center/cover no-repeat`
            : 'var(--color-neutral-200, #e4e4e7)',
        }}
      />
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 'var(--font-size-base, 15px)', fontWeight: 600, lineHeight: 1.3 }}>
          {titleWithNumber}
        </div>
        {episode.description && style === 'cards' && (
          <div
            style={{
              fontSize: '13px',
              color: 'var(--color-text-secondary, #71717a)',
              marginTop: 4,
              lineHeight: 1.5,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical' as const,
              overflow: 'hidden',
            }}
          >
            {episode.description}
          </div>
        )}
      </div>
      <div
        style={{
          alignSelf: 'center',
          fontSize: 'var(--font-size-xs, 12px)',
          color: 'var(--color-text-secondary, #71717a)',
        }}
      >
        {fmtDuration(episode.durationSeconds) ?? episode.durationLabel ?? ''}
      </div>
    </Link>
  );
}
